/**
 * What each role holds.
 *
 * Read-only, and it will stay read-only for now: roles are platform-owned in this phase
 * and a firm composing its own permission sets is a later change (CR-31). There is no
 * write verb on the resource, so there is no control here that suggests one.
 *
 * The permission keys are shown in full rather than summarised into adjectives. An
 * administrator choosing a role for a new hire is making an access-control decision, and
 * "Lawyer" tells them nothing about whether that person will be able to see another
 * department's matters.
 *
 * A permission is `module.action.scope`, narrowest scope to widest: own, assigned, team,
 * department, organization, platform. Scopes widen additively — a role holding two gets
 * the union — and there are no negative grants anywhere in this model.
 */

import { Badge, Card, CardBody, CardHeader, ErrorState, Skeleton } from '@legalcrm/ui';

import { useRoleCatalogue } from './useRoleCatalogue';

/** Groups the keys by module so a long list reads as structure rather than as noise. */
function groupByModule(keys: string[]): Array<[string, string[]]> {
  const groups = new Map<string, string[]>();
  for (const key of keys) {
    const [module = key] = key.split('.');
    const bucket = groups.get(module) ?? [];
    bucket.push(key);
    groups.set(module, bucket);
  }
  return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
}

export function RolesPage() {
  const catalogue = useRoleCatalogue();

  if (catalogue.isLoading) {
    return (
      <div className="flex flex-col gap-3" aria-busy="true">
        <span className="sr-only">Loading roles</span>
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (catalogue.error) {
    return <ErrorState error={catalogue.error} onRetry={catalogue.refetch} />;
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-ink-muted">
        A permission is <code className="font-mono">module.action.scope</code>. Holding one
        is never enough on its own: the server checks the capability, then resolves
        whether the record in front of you falls inside the granted scope. Role never
        equals access.
      </p>

      {catalogue.roles.map((role) => (
        <Card key={role.id}>
          <CardHeader
            title={role.name}
            description={role.description || undefined}
            actions={
              <div className="flex items-center gap-2">
                <Badge tone="neutral" size="sm">
                  {role.key}
                </Badge>
                {role.is_custom && (
                  <Badge tone="info" size="sm">
                    This firm’s own
                  </Badge>
                )}
              </div>
            }
          />
          <CardBody>
            {role.permission_keys.length === 0 ? (
              <p className="text-sm text-ink-subtle">
                This role confers no capabilities at all.
              </p>
            ) : (
              <dl className="flex flex-col gap-2.5">
                {groupByModule(role.permission_keys).map(([module, keys]) => (
                  <div key={module} className="flex flex-wrap items-baseline gap-2">
                    <dt className="w-32 shrink-0 text-xs font-medium uppercase tracking-wide text-ink-subtle">
                      {module.replace(/_/g, ' ')}
                    </dt>
                    <dd className="flex flex-1 flex-wrap gap-1">
                      {keys.map((key) => (
                        <Badge key={key} tone="neutral" size="sm">
                          {key.split('.').slice(1).join('.')}
                        </Badge>
                      ))}
                    </dd>
                  </div>
                ))}
              </dl>
            )}
          </CardBody>
        </Card>
      ))}
    </div>
  );
}
